import Hls from 'hls.js';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { buildHlsConfig } from '#common/hls-key-deobfuscate.ts';
import { useMediaAccess } from '#common/hooks/useMediaAccess.ts';

/**
 * Derive the CloudFront asset path from an HLS manifest URL.
 * e.g. "/media/hls/abc123/video/playlist.m3u8" → "media/hls/abc123/video"
 *      "/_/media/media/hls/abc123/video/playlist.m3u8" → "media/hls/abc123/video"
 */
function hlsUrlToAssetPath(url: string): string | null {
    if (!url.includes('.m3u8')) {
        return null;
    }
    // Strip leading slash and dev-only /_/media/ prefix
    let p = url.replace(/^\//, '').replace(/^_\/media\//, '');
    // Strip trailing /playlist.m3u8 (or any .m3u8 filename)
    p = p.replace(/\/[^/]+\.m3u8$/, '');
    return p || null;
}

interface Props {
    children: ReactNode;
}

interface PendingAudio {
    originId: string;
    url: string;
}

interface AudioContextProps {
    playingAudioOriginId: string;
    pendingAudio: PendingAudio | null;
    playAudio: (originId: string, audio: Blob | string) => void;
    stopAudio: (originId: string) => void;
    retryPendingAudio: () => void;
    clearPendingAudio: () => void;
}

const AudioContext = createContext<AudioContextProps>({} as AudioContextProps);

let counter = 0;
const generateUniqueValue = () => `unique_value_${counter++}`;

export const useAudio = () => {
    // Generate a unique ID for each consumer of the hook
    const [ originId ] = useState(generateUniqueValue());
    const context = useContext(AudioContext);
    if (!context) {
        throw new Error('useAudio must be used within an AudioProvider');
    }
    const playAudio = useCallback((audio: Blob | string) => {
        context.playAudio(originId, audio);
    }, [ context, originId ]);

    const stopAudio = useCallback(() => {
        context.stopAudio(originId);
    }, [ context, originId ]);

    // Check if this origin has pending audio that failed to autoplay
    const hasPendingAudio = context.pendingAudio?.originId === originId;

    return {
        isOtherPlayingAudio: originId !== context.playingAudioOriginId && context.playingAudioOriginId !== '',
        isPlayingAudio: context.playingAudioOriginId === originId,
        hasPendingAudio,
        playAudio,
        stopAudio,
        retryPendingAudio: context.retryPendingAudio,
        clearPendingAudio: context.clearPendingAudio
    };
};

export function AudioProvider({ children }: Props) {
    const [ playingAudioOriginId, setPlayingAudioOriginId ] = useState('');
    const [ pendingAudio, setPendingAudio ] = useState<PendingAudio | null>(null);
    const audioElementRef = useRef<HTMLAudioElement | null>(null);
    const hlsRef = useRef<Hls | null>(null);
    const [ currentAudioUrl, setCurrentAudioUrl ] = useState<string | null>(null);
    const currentOriginIdRef = useRef<string | null>(null);
    const { requestAccess } = useMediaAccess();

    // Clean up audio element, HLS instance, and Blob URLs only on unmount
    useEffect(() => {
        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
            if (audioElementRef.current) {
                audioElementRef.current.pause();
                audioElementRef.current.src = '';
            }
            if (currentAudioUrl && currentAudioUrl.startsWith('blob:')) {
                URL.revokeObjectURL(currentAudioUrl);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on unmount

    // Handler for when audio playback ends
    const handleAudioEnded = useCallback(() => {
        setPlayingAudioOriginId('');
        currentOriginIdRef.current = null;
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }
        if (audioElementRef.current) {
            audioElementRef.current.src = '';
        }
        if (currentAudioUrl && currentAudioUrl.startsWith('blob:')) {
            URL.revokeObjectURL(currentAudioUrl);
            setCurrentAudioUrl(null);
        }
    }, [ currentAudioUrl ]);

    const handleAudioPlaying = useCallback(() => {
        const originId = currentOriginIdRef.current;
        if (!originId) {
            return;
        }
        setPendingAudio(current => current?.originId === originId ? null : current);
        setPlayingAudioOriginId(originId);
    }, []);

    const playAudio = useCallback(async (originId: string, audio: Blob | string) => {
        if (typeof window === 'undefined') {
            return;
        }
        if (!(typeof audio === 'string' ? audio.length > 0 : audio instanceof Blob && audio.size > 0)) {
            console.warn('Skipping audio playback because the audio payload is empty or invalid.');
            return;
        }
        // Stop any currently playing audio and destroy HLS instance
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }
        if (audioElementRef.current) {
            audioElementRef.current.pause();
            audioElementRef.current.src = '';
        }
        if (currentAudioUrl && currentAudioUrl.startsWith('blob:')) {
            URL.revokeObjectURL(currentAudioUrl);
            setCurrentAudioUrl(null);
        }
        currentOriginIdRef.current = originId;
        let audioUrl: string;
        if (typeof audio === 'string') {
            audioUrl = audio;
        } else {
            audioUrl = URL.createObjectURL(audio);
            setCurrentAudioUrl(audioUrl);
        }
        if (!audioElementRef.current) {
            audioElementRef.current = new window.Audio();
            audioElementRef.current.addEventListener('ended', handleAudioEnded);
            audioElementRef.current.addEventListener('playing', handleAudioPlaying);
        }

        // Use hls.js for .m3u8 sources
        const isHls = typeof audio === 'string' && audio.includes('.m3u8');
        if (isHls && Hls.isSupported()) {
            // Request CloudFront signed cookies before loading HLS manifest
            const assetPath = hlsUrlToAssetPath(audioUrl);
            if (assetPath) {
                await requestAccess(assetPath);
            }

            const hls = new Hls(buildHlsConfig(Hls));
            hls.loadSource(audioUrl);
            hls.attachMedia(audioElementRef.current);
            hlsRef.current = hls;

            hls.on(Hls.Events.MANIFEST_PARSED, async () => {
                if (currentOriginIdRef.current !== originId) {
                    return;
                }
                try {
                    await audioElementRef.current!.play();
                    if (currentOriginIdRef.current !== originId) {
                        return;
                    }
                    setPendingAudio(null);
                    setPlayingAudioOriginId(originId);
                    currentOriginIdRef.current = originId;
                } catch (error) {
                    if (currentOriginIdRef.current !== originId) {
                        return;
                    }
                    if (!audioElementRef.current!.paused) {
                        setPendingAudio(null);
                        setPlayingAudioOriginId(originId);
                        return;
                    }
                    console.warn('Audio autoplay was blocked:', error);
                    setPendingAudio({ originId, url: audioUrl });
                    if (typeof audio !== 'string') {
                        setCurrentAudioUrl(audioUrl);
                    }
                }
            });
            return;
        }

        audioElementRef.current.src = audioUrl;
        audioElementRef.current.currentTime = 0;
        try {
            const playPromise = audioElementRef.current.play();
            if (playPromise !== undefined) {
                await playPromise;
            }
            if (currentOriginIdRef.current !== originId) {
                return;
            }
            // Clear any pending audio on successful play
            setPendingAudio(null);
            setPlayingAudioOriginId(originId);
            currentOriginIdRef.current = originId;
        } catch (error) {
            if (currentOriginIdRef.current !== originId) {
                return;
            }
            if (!audioElementRef.current.paused) {
                setPendingAudio(null);
                setPlayingAudioOriginId(originId);
                return;
            }
            // Autoplay was blocked (common on iOS/Safari)
            // Store the pending audio so user can manually trigger playback
            console.warn('Audio autoplay was blocked:', error);
            setPendingAudio({ originId, url: audioUrl });
            // Keep the audio URL stored so we don't revoke it
            if (typeof audio !== 'string') {
                setCurrentAudioUrl(audioUrl);
            }
        }
    }, [ currentAudioUrl, handleAudioEnded, handleAudioPlaying, requestAccess ]);

    const stopAudio = useCallback((originId: string) => {
        if (playingAudioOriginId === originId && audioElementRef.current) {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
            audioElementRef.current.pause();
            audioElementRef.current.src = '';
            setPlayingAudioOriginId('');
            currentOriginIdRef.current = null;
            if (currentAudioUrl && currentAudioUrl.startsWith('blob:')) {
                URL.revokeObjectURL(currentAudioUrl);
                setCurrentAudioUrl(null);
            }
        }
    }, [ playingAudioOriginId, currentAudioUrl ]);

    // Retry playing pending audio - must be called from a user interaction (click/tap)
    const retryPendingAudio = useCallback(async () => {
        if (!pendingAudio || !audioElementRef.current) {
            return;
        }
        try {
            // Ensure the audio element has the correct source
            if (audioElementRef.current.src !== pendingAudio.url) {
                audioElementRef.current.src = pendingAudio.url;
            }
            audioElementRef.current.currentTime = 0;
            await audioElementRef.current.play();
            setPlayingAudioOriginId(pendingAudio.originId);
            currentOriginIdRef.current = pendingAudio.originId;
            setPendingAudio(null);
        } catch (error) {
            console.error('Failed to play audio even with user interaction:', error);
        }
    }, [ pendingAudio ]);

    // Clear pending audio without playing
    const clearPendingAudio = useCallback(() => {
        if (pendingAudio && pendingAudio.url.startsWith('blob:')) {
            URL.revokeObjectURL(pendingAudio.url);
        }
        setPendingAudio(null);
    }, [ pendingAudio ]);

    const value: AudioContextProps = {
        playingAudioOriginId,
        pendingAudio,
        playAudio,
        stopAudio,
        retryPendingAudio,
        clearPendingAudio
    };

    return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>;
}
